/**
 * The pure half of the "AI calls out" tab: which lines the card says about
 * itself, and what its form is allowed to send.
 *
 * React-free for the same reason `aiAgentNotes.ts` is: the part worth testing
 * is deciding how loudly to say each gap, and making sure the form sends only
 * what it owns. The PUT schema checks before anything is clamped, so every box
 * is read through the same bounded readers the answering tab uses, and the
 * patch carries only the fields that differ from what the server last said.
 * That second rule matters more here than there: a form left open over a stop
 * ("Peata kõik" writes `enabled` and `autopilot` off) must not switch the AI
 * back on by re-sending a stale switch, and an unchanged test-number list must
 * not send the save through a Twilio lookup.
 */
import {
  OUTBOUND_BLOCKERS,
  OUTBOUND_COOLDOWN_DAYS,
  OUTBOUND_DAILY_CAP,
  OUTBOUND_MAX_CALL_SEC,
  OUTBOUND_MAX_TEST_NUMBERS,
  OUTBOUND_MIN_GAP_SEC,
  OUTBOUND_RING_SECONDS,
  OUTBOUND_TEST_DAILY_CAP,
  type AiOutboundSettings,
  type AiOutboundSettingsPatch,
  type AiOutboundStatus,
  type OutboundBlocker,
} from "../../../../../lib/aiOutbound";
import type { BusinessDay } from "../../../../../lib/telephonyHours";
import { readBoundedInt, readTestCallers, type IntBounds, type TestCallersRead } from "../aiAgent/aiAgentNotes";

// ---------------------------------------------------------------- blockers

export interface OutboundNote {
  /** The server's code, verbatim. An unknown one is printed in its note. */
  blocker: string;
  tone: "bad" | "warn";
  /** Suffix under `settings.aiOutbound.blocker`: the code itself, or "unknown". */
  key: string;
}

/**
 * The two gaps that are a decision still open on this very page: the switch,
 * and the test numbers test mode rings. Everything else is a piece the AI
 * cannot call without, and reads as a fault.
 */
const OPEN_DECISIONS: ReadonlySet<string> = new Set<OutboundBlocker>(["disabled", "no_test_numbers"]);
const KNOWN: ReadonlySet<string> = new Set<string>(OUTBOUND_BLOCKERS);

/**
 * One note per blocker, in the server's order. A code this build does not know
 * still gets a line, so a newer server's gap never reads as no gap at all.
 */
export function outboundBlockerNotes(blockers: readonly string[] | undefined): OutboundNote[] {
  if (!blockers) return [];
  const seen = new Set<string>();
  const notes: OutboundNote[] = [];
  for (const blocker of blockers) {
    if (seen.has(blocker)) continue;
    seen.add(blocker);
    notes.push({
      blocker,
      tone: OPEN_DECISIONS.has(blocker) ? "warn" : "bad",
      key: KNOWN.has(blocker) ? blocker : "unknown",
    });
  }
  return notes;
}

/**
 * The gaps the settings card names: what stops a live call, plus what stops
 * only the company card's test call.
 *
 * The second list matters with test mode off. Automation then needs no test
 * number, so the live list is silent about it, while the button still rings
 * nothing else and is blocked. Saying it only on the company card would leave
 * the one page where it is fixed blind to it. Known codes are put in the fix
 * order; a code this build does not know follows them, in the server's order.
 */
export function outboundCardBlockers(status: Pick<AiOutboundStatus, "blockers" | "testCallBlockers"> | null): string[] {
  if (!status) return [];
  const all: string[] = [...(status.blockers ?? []), ...(status.testCallBlockers ?? [])];
  const present = new Set(all);
  const known = OUTBOUND_BLOCKERS.filter((code) => present.has(code));
  const unknown = [...new Set(all.filter((code) => !KNOWN.has(code)))];
  return [...known, ...unknown];
}

// ---------------------------------------------------------------- form

/** The test-number box as the list the server will keep (at most five). */
export function readTestNumbers(text: string): TestCallersRead {
  return readTestCallers(text, OUTBOUND_MAX_TEST_NUMBERS);
}

/** The number boxes on the form. */
export type OutboundLimitKey = "dailyCap" | "testDailyCap" | "ringSeconds" | "maxCallSec" | "cooldownDays";

/**
 * Each number box with the bounds it is read inside, in the order the form
 * draws them: the test ceiling first, because test mode ships on and the first
 * calls anyone places are test calls. Each key is also its label under
 * `settings.aiOutbound`, and the key plus `Hint` its hint. The gap between
 * calls is not here: its label lives under `settings.aiOutbound.automation`.
 */
export const OUTBOUND_LIMITS: readonly { key: OutboundLimitKey; bounds: IntBounds }[] = [
  { key: "testDailyCap", bounds: OUTBOUND_TEST_DAILY_CAP },
  { key: "dailyCap", bounds: OUTBOUND_DAILY_CAP },
  { key: "cooldownDays", bounds: OUTBOUND_COOLDOWN_DAYS },
  { key: "ringSeconds", bounds: OUTBOUND_RING_SECONDS },
  { key: "maxCallSec", bounds: OUTBOUND_MAX_CALL_SEC },
];

/** Every box as the text the operator typed; converted only when saving. */
export interface OutboundDraft {
  enabled: boolean;
  /** The automation works the active campaigns by itself (R4). */
  autopilot: boolean;
  testMode: boolean;
  /** One number per line, as typed. */
  testNumbers: string;
  /** "" is the main number. */
  fromNumber: string;
  dailyCap: string;
  testDailyCap: string;
  ringSeconds: string;
  maxCallSec: string;
  machineDetection: boolean;
  cooldownDays: string;
  /** The calling window, seven days with Sunday at index 0, as the week editor holds it. */
  days: BusinessDay[];
  timezone: string;
  skipHolidays: boolean;
  /** Seconds between the starts of two AI calls, as typed. Not in `OUTBOUND_LIMITS`: its label lives under `automation`. */
  minGapSec: string;
  /** The AI sends a real call's defect report letter to the company itself. */
  reportMailLive: boolean;
  /** The test inbox, as typed. */
  reportMailTestInbox: string;
}

export function toOutboundDraft(s: AiOutboundSettings): OutboundDraft {
  return {
    enabled: s.enabled,
    autopilot: s.autopilot,
    testMode: s.testMode,
    testNumbers: s.testNumbers.join("\n"),
    fromNumber: s.fromNumber,
    dailyCap: String(s.dailyCap),
    testDailyCap: String(s.testDailyCap),
    ringSeconds: String(s.ringSeconds),
    maxCallSec: String(s.maxCallSec),
    machineDetection: s.machineDetection,
    cooldownDays: String(s.cooldownDays),
    days: s.days.map((day) => ({ ...day })),
    timezone: s.timezone,
    skipHolidays: s.skipHolidays,
    minGapSec: String(s.minGapSec),
    reportMailLive: s.reportMailLive === true,
    reportMailTestInbox: s.reportMailTestInbox ?? "",
  };
}

/** The server's own shape check for the inbox (`settings-report-mail.ts`): shallow on purpose. */
const INBOX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The test inbox box as the server will keep it: trimmed, lower case, and
 * `invalid` when something is typed that is not an address. An invalid one is
 * not sent - the saved inbox stands - and the form says so beside the box,
 * because the server's refusal would name a field, not the typo.
 */
export function readReportInbox(text: string): { value: string; invalid: boolean } {
  const value = text.trim().toLowerCase();
  if (!value) return { value: "", invalid: false };
  return INBOX.test(value) && value.length <= 254 ? { value, invalid: false } : { value, invalid: true };
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

function sameWeek(a: readonly BusinessDay[], b: readonly BusinessDay[]): boolean {
  return a.length === b.length && a.every((day, i) => day.open === b[i]?.open && day.from === b[i]?.from && day.to === b[i]?.to);
}

/**
 * The weekdays (`getDay()` indexes) switched on whose end is not after their
 * start: 24:00 (stored as 00:00), the same minute twice, or a window past
 * midnight. The week editor allows all three for the phone line's hours, and
 * the server reads each as CLOSED for AI calls (`parseDays` in `settings.ts`),
 * so the form has to say so before the save quietly switches the day off.
 */
export function closedWindowDays(days: readonly BusinessDay[]): number[] {
  return days.flatMap((day, weekday) => (day.open && day.to <= day.from ? [weekday] : []));
}

/**
 * The draft as a merge patch the schema cannot refuse, holding only what
 * differs from `saved` (the settings the server last answered with).
 *
 * The retention ages are never in it: they are stated, not edited. The calling
 * window goes whole (seven days) when any day differs, as the schema asks for
 * exactly seven. An empty patch is a valid save that changes nothing and
 * returns the current verdict.
 */
export function toOutboundPatch(d: OutboundDraft, saved: AiOutboundSettings): AiOutboundSettingsPatch {
  const patch: AiOutboundSettingsPatch = {};
  if (d.enabled !== saved.enabled) patch.enabled = d.enabled;
  if (d.autopilot !== saved.autopilot) patch.autopilot = d.autopilot;
  if (d.testMode !== saved.testMode) patch.testMode = d.testMode;
  const testNumbers = readTestNumbers(d.testNumbers).numbers;
  if (!sameList(testNumbers, saved.testNumbers)) patch.testNumbers = testNumbers;
  const fromNumber = d.fromNumber.trim();
  if (fromNumber !== saved.fromNumber) patch.fromNumber = fromNumber;
  for (const { key, bounds } of OUTBOUND_LIMITS) {
    const value = readBoundedInt(d[key], bounds);
    if (value !== saved[key]) patch[key] = value;
  }
  if (d.machineDetection !== saved.machineDetection) patch.machineDetection = d.machineDetection;
  if (d.days.length === 7 && !sameWeek(d.days, saved.days)) patch.days = d.days.map((day) => ({ ...day }));
  // A blank zone would be a 400 (the schema wants a known one); the saved zone stands.
  const timezone = d.timezone.trim();
  if (timezone && timezone !== saved.timezone) patch.timezone = timezone;
  if (d.skipHolidays !== saved.skipHolidays) patch.skipHolidays = d.skipHolidays;
  const minGapSec = readBoundedInt(d.minGapSec, OUTBOUND_MIN_GAP_SEC);
  if (minGapSec !== saved.minGapSec) patch.minGapSec = minGapSec;
  if (d.reportMailLive !== (saved.reportMailLive === true)) patch.reportMailLive = d.reportMailLive;
  const inbox = readReportInbox(d.reportMailTestInbox);
  if (!inbox.invalid && inbox.value !== (saved.reportMailTestInbox ?? "")) patch.reportMailTestInbox = inbox.value;
  return patch;
}

// ---------------------------------------------------------------- calling number

export interface FromNumberOption {
  value: string;
  /** "main" is the "" row, "owned" a bought number, "saved" the stored value the list lacks. */
  kind: "main" | "owned" | "saved";
}

/**
 * The calling-number menu: the main number first, then the numbers bought on
 * Twilio, then the stored value when the list does not hold it.
 *
 * The main number is not listed a second time among the bought ones: its own
 * row already stands for it and follows the account setting. It stays when it
 * is the stored value, so a pinned choice is not shown as missing. The stored
 * row matters when Twilio gave no list (`owned` null) or the number was
 * released: a blank menu would read as the main number, which is not what the
 * AI is calling from.
 */
export function fromNumberOptions(saved: string, owned: readonly string[] | null, mainNumber: string): FromNumberOption[] {
  const current = saved.trim();
  const options: FromNumberOption[] = [{ value: "", kind: "main" }];
  const seen = new Set<string>([""]);
  for (const number of owned ?? []) {
    if (seen.has(number)) continue;
    if (number === mainNumber && number !== current) continue;
    seen.add(number);
    options.push({ value: number, kind: "owned" });
  }
  if (current && !seen.has(current)) options.push({ value: current, kind: "saved" });
  return options;
}
